import React, { useCallback, memo, useMemo } from 'react';
import { styled } from '@compiled/react';
import DropdownMenu, { DropdownItemCheckbox } from '@atlaskit/dropdown-menu';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { FIELDS_DROPDOWN_TEST_ID_PREFIX } from '../../../common/constants';
import type { ColumnName } from '../../../common/types';
import { useColumnsVisibleState, useMappingState } from '../../../controllers/import-state';
import messages from './messages';

export const FieldsDropdown = memo<{ testId?: string }>(({ testId }) => {
	const { formatMessage } = useIntl();
	const [mappingState] = useMappingState();
	const [
		{ numColumnsVisible, numColumnsHidden, areAllColumnsVisible },
		{ setIsVisibleForAll, setIsVisibleForColumn },
	] = useColumnsVisibleState();

	const onToggleAllColumnsVisible = useCallback(() => {
		setIsVisibleForAll(!areAllColumnsVisible);
	}, [areAllColumnsVisible, setIsVisibleForAll]);

	const getOnToggleIsVisibleForColumn = useCallback(
		(colName: ColumnName) => () => {
			setIsVisibleForColumn(colName, !mappingState?.columns[colName]?.isVisible);
		},
		[mappingState?.columns, setIsVisibleForColumn],
	);

	const dropdownItems = useMemo(
		() =>
			mappingState?.columnOrder
				.map((colName) => mappingState.columns[colName])
				.map((col, index) => (
					<DropdownItemCheckbox
						id={col.columnName}
						key={`${col.columnName}-${index}`}
						isSelected={col.isVisible}
						onClick={getOnToggleIsVisibleForColumn(col.columnName)}
						testId={`${FIELDS_DROPDOWN_TEST_ID_PREFIX}.${col.columnName}-${index}`}
					>
						{col.columnName}
					</DropdownItemCheckbox>
				)),
		[getOnToggleIsVisibleForColumn, mappingState?.columnOrder, mappingState?.columns],
	);

	const getTriggerContent = useCallback(() => {
		if (numColumnsHidden === 0) {
			return formatMessage(messages.includedColumns, {
				count: numColumnsVisible,
			});
		}

		return `${formatMessage(messages.includedColumns, {
			count: numColumnsVisible,
		})} / ${formatMessage(messages.ignoredColumns, {
			count: numColumnsHidden,
		})}`;
	}, [formatMessage, numColumnsHidden, numColumnsVisible]);

	return (
		<Wrapper data-testid="project-create-importer-upload-flow.ui.mapping-screen.fields-dropdown.wrapper">
			<HeadingWrapper>{formatMessage(messages.importData)}</HeadingWrapper>
			<DropdownMenu
				trigger={getTriggerContent()}
				isLoading={mappingState === null}
				shouldFlip={false}
				testId={testId}
			>
				<DropdownItemsWrapper>
					<DropdownItemCheckbox
						id="All Fields"
						isSelected={areAllColumnsVisible}
						onClick={onToggleAllColumnsVisible}
						testId={`${FIELDS_DROPDOWN_TEST_ID_PREFIX}.all-fields`}
					>
						{formatMessage(messages.allColumns)}
					</DropdownItemCheckbox>
					{dropdownItems}
				</DropdownItemsWrapper>
			</DropdownMenu>
		</Wrapper>
	);
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	maxHeight: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeadingWrapper = styled.span({
	marginRight: token('space.300', '24px'),
	fontWeight: 600,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle', colors.N300),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropdownItemsWrapper = styled.div({
	maxHeight: '30vh',
});
